'use client';

/**
 * Cookie consent storage + event helpers.
 *
 * The legal requirement (EU ePrivacy / GDPR; tightening in AU) is that
 * non-essential cookies and similar trackers must be opt-in. PostHog is the
 * only non-essential category we load — gating its init on the choice stored
 * here keeps us compliant without splitting the analytics module into
 * "consented" and "unconsented" code paths.
 *
 * Storage uses localStorage (not a cookie) so the choice itself isn't sent
 * back to the server and doesn't itself trigger the "first-party cookie"
 * question. Versioned so we can re-prompt cleanly if categories change.
 */

export const CONSENT_STORAGE_KEY = 'cookieConsent';
export const CONSENT_VERSION = 1;
export const CONSENT_CHANGED_EVENT = 'cookie-consent-changed';
export const CONSENT_REOPEN_EVENT = 'cookie-consent-reopen';

export type ConsentChoice = {
  analytics: boolean;
  version: number;
  decidedAt: string;
};

export function readConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentChoice>;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.analytics !== 'boolean') return null;
    return {
      analytics: parsed.analytics,
      version: CONSENT_VERSION,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeConsent(
  choice: Pick<ConsentChoice, 'analytics'>,
): ConsentChoice {
  const full: ConsentChoice = {
    analytics: choice.analytics,
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full));
  } catch {
    // localStorage can throw in Safari private mode or sandboxed iframes.
    // The in-memory broadcast below still lets the current tab apply the
    // choice for the session, which is the best we can do without storage.
  }
  window.dispatchEvent(
    new CustomEvent<ConsentChoice>(CONSENT_CHANGED_EVENT, { detail: full }),
  );
  return full;
}

/**
 * Ask the consent banner to reopen so a user can review or change their choice
 * after the first decision (e.g. from the footer "Cookie preferences" link).
 * The banner listens for this event and shows itself with the category toggles
 * expanded, pre-filled from the stored choice.
 */
export function openCookiePreferences(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
}
